/**
 * FilterBar — instant search + genre dropdown for a collection page.
 *
 * NOTE (2026-08): Category chips (All/Latest/Premium/Free/Originals) have
 * been removed as of the Collection Redesign. Premium/Free are now
 * first-class routes (`/music/premium`, `/music/free`) and Originals is
 * a per-collection toggle rendered outside this component. FilterBar
 * now handles ONLY: genre dropdown + debounced free-text search.
 *
 * The `lockCategory` prop is retained (no-op) for callers that still
 * pass it — it is now the default and only mode.
 */
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Search, X, Filter, ChevronDown } from "lucide-react";

/**
 * @param {object} props
 * @param {{category:string, genre:string, q:string}} props.value - Controlled filter state.
 * @param {(next:{category:string, genre:string, q:string})=>void} props.onChange
 * @param {Array<{genre:string, count:number}>} [props.genres] - Populates the genre dropdown.
 * @param {boolean} [props.lockCategory] - (deprecated, always true — kept for callers)
 * @param {string} [props.testidPrefix] - Prefix for data-testid attributes.
 * @param {number} [props.resultCount] - Optional count shown next to the Clear button.
 * @param {string} [props.searchPlaceholder] - Override the default search placeholder.
 */
export default function FilterBar({
  value,
  onChange,
  genres = [],
  hideGenre = false,
  lockCategory: _lockCategory = true, // eslint-disable-line no-unused-vars
  testidPrefix = "catalog-filter",
  resultCount = null,
  searchPlaceholder,
}) {
  const { t } = useTranslation();
  const [rawQuery, setRawQuery] = useState(value.q || "");
  const debounceRef = useRef(null);

  // Keep local input in sync when the parent externally clears the query.
  useEffect(() => {
    if ((value.q || "") !== rawQuery) setRawQuery(value.q || "");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value.q]);

  const commit = (patch) => onChange({ ...value, ...patch });

  const onQueryChange = (v) => {
    setRawQuery(v);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => commit({ q: v }), 250);
  };

  const isActive = Boolean(value.genre || value.q);

  const clear = () => {
    setRawQuery("");
    onChange({ ...value, genre: "", q: "" });
  };

  const effectivePlaceholder = searchPlaceholder || t("filter.searchPlaceholder");

  return (
    <div
      data-testid={testidPrefix}
      className="mt-6 mb-6 rounded-2xl border border-white/10 bg-white/[0.02] px-4 py-4 md:px-5 md:py-4"
    >
      <div className="flex items-center gap-3 flex-wrap">
        {/* Genre dropdown (hidden on /music/edits: regional chips are its filters) */}
        {!hideGenre && (<div className="relative">
          <select
            data-testid={`${testidPrefix}-genre`}
            value={value.genre || ""}
            onChange={(e) => commit({ genre: e.target.value })}
            className="appearance-none bg-black/40 border border-white/15 hover:border-white/40 focus:border-red-brand focus:outline-none rounded-full pl-4 pr-9 py-2 text-xs font-mono tracking-widest text-white/80 cursor-pointer transition-colors"
            aria-label={t("filter.genreLabel")}
          >
            <option value="">{t("filter.allGenres")}</option>
            {genres.map((g) => (
              <option key={g.genre} value={g.genre}>
                {g.genre.toUpperCase()} ({g.count})
              </option>
            ))}
          </select>
          <ChevronDown
            size={12}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-white/50 pointer-events-none"
          />
        </div>)}

        {/* Search input */}
        <label className="flex-1 min-w-[180px] relative">
          <Search
            size={14}
            className="absolute left-3.5 top-1/2 -translate-y-1/2 text-white/40 pointer-events-none"
          />
          <input
            type="search"
            data-testid={`${testidPrefix}-search`}
            value={rawQuery}
            onChange={(e) => onQueryChange(e.target.value)}
            placeholder={effectivePlaceholder}
            aria-label={t("filter.searchLabel")}
            className="w-full bg-black/40 border border-white/15 hover:border-white/40 focus:border-red-brand focus:outline-none rounded-full pl-10 pr-9 py-2 text-sm text-white placeholder:text-white/40 transition-colors"
          />
          {rawQuery && (
            <button
              type="button"
              data-testid={`${testidPrefix}-search-clear`}
              onClick={() => onQueryChange("")}
              aria-label={t("filter.clearSearch")}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-white/50 hover:text-white"
            >
              <X size={14} />
            </button>
          )}
        </label>

        {(isActive || resultCount !== null) && (
          <div
            className="flex items-center gap-3 text-[10px] font-mono tracking-widest text-white/50"
            data-testid={`${testidPrefix}-status`}
          >
            {resultCount !== null && (
              <span data-testid={`${testidPrefix}-count`}>
                <Filter size={10} className="inline mr-1 text-red-brand" />
                {t("filter.results", { count: resultCount })}
              </span>
            )}
            {isActive && (
              <button
                onClick={clear}
                data-testid={`${testidPrefix}-clear`}
                className="inline-flex items-center gap-1 text-red-brand hover:text-white transition-colors"
              >
                {t("filter.clear")} <X size={11} />
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
